import { isWeb } from '@gluestack-ui/nativewind-utils/IsWeb';
import { useCallback, useState, type Ref } from 'react';

import { PointRow } from './PointRow';
import { REPLY_DRAFT_TOUR_ZONES } from '../../model/tour';
import type { ClientEvent, ReplyDraft, Segment } from '../../model/types';
import { REPLY_DRAFT_COPY } from '../copy';
import { SectionHeading } from '../shared/SectionHeading';
import { TextEntry } from '../shared/TextEntry';
import { announce } from '../shared/announce';
import type { HeadingNode } from '../shared/focusHeading';
import { ReplyDraftTourZone } from '../tour/ReplyDraftTourZone';

import { Heading } from '@/components/ui/heading';
import { Text } from '@/components/ui/text';
import { VStack } from '@/components/ui/vstack';

const OTHER_OPTION_KEY = 'other';

type DraftPointsProps = {
  draft: ReplyDraft;
  isAddingNote: boolean;
  onTap: (event: ClientEvent) => void;
  onAddNote: (text: string) => Promise<boolean>;
  noteText: string;
  onNoteTextChange: (text: string) => void;
  headingRef?: Ref<HeadingNode>;
};

/**
 * Splits the points into the two lists in one pass, keeping the server's order
 * in each, and counts the draft's own points for the heading. The expert's
 * notes are listed but not counted, since they were never the draft's to offer.
 */
function partitionPoints(segments: Segment[]) {
  const inReply: Segment[] = [];

  const notInReply: Segment[] = [];

  let draftedCount = 0;

  let draftedInReplyCount = 0;

  for (const segment of segments) {
    (segment.struck ? notInReply : inReply).push(segment);

    if (segment.origin !== 'voice') {
      draftedCount += 1;

      draftedInReplyCount += segment.struck ? 0 : 1;
    }
  }

  return { inReply, notInReply, draftedCount, draftedInReplyCount };
}

function callLabel(draft: ReplyDraft): string {
  const optionKey = draft.draft?.optionKey;

  if (optionKey === OTHER_OPTION_KEY) {
    return REPLY_DRAFT_COPY.somethingElse;
  }

  return draft.card?.options.find((option) => option.key === optionKey)?.label ?? '';
}

/**
 * The wizard's points step: the chosen position named at the top, then the
 * points in two lists, those in the reply and those not, each with a minus to
 * take it out or a plus to put it in, then a field for the expert's own.
 *
 * The server lists the drafted sentences, the detailed ones and its
 * suggestions together, with everything beyond the usual reply already out.
 * So adding a suggestion is the same plus as adding back a removed point,
 * every sentence is written before the expert sees it, and nothing waits on
 * the server to appear. A must-fix suggestion says so on its row.
 *
 * Two labeled lists replaced one list with the left-out points struck
 * through: the strike made those points hard to read, and a heading says
 * plainly which points the reply holds. A point moves between the lists when
 * changed, keeping the server's order within each, and every move is
 * announced, so a screen reader hears where it went.
 *
 * The step heading carries how many of the draft's points are in the reply,
 * so the count is heard without counting rows; the expert's own notes are
 * listed but not counted, since they were never the draft's to offer.
 */
export function DraftPoints({
  draft,
  isAddingNote,
  onTap,
  onAddNote,
  noteText,
  onNoteTextChange,
  headingRef,
}: DraftPointsProps) {
  const state = draft.draft;

  const [announcement, setAnnouncement] = useState('');

  const speak = useCallback((message: string) => {
    announce(message);

    setAnnouncement(message);
  }, []);

  const remove = useCallback(
    (segment: Segment) => {
      onTap({ type: 'segment_struck', segmentId: segment.id });

      speak(REPLY_DRAFT_COPY.removedAnnouncement(segment.text));
    },
    [onTap, speak]
  );

  const add = useCallback(
    (segment: Segment) => {
      onTap({ type: 'segment_restored', segmentId: segment.id });

      speak(REPLY_DRAFT_COPY.addedAnnouncement(segment.text));
    },
    [onTap, speak]
  );

  const swap = useCallback(
    (segment: Segment) => onTap({ type: 'swap_cycled', segmentId: segment.id }),
    [onTap]
  );

  if (!state) {
    return null;
  }

  const { inReply, notInReply, draftedCount, draftedInReplyCount } = partitionPoints(
    state.segments
  );

  const renderRows = (segments: Segment[]) =>
    segments.map((segment) => (
      <PointRow key={segment.id} segment={segment} onRemove={remove} onAdd={add} onSwap={swap} />
    ));

  return (
    <VStack className="gap-6" testID="reply-draft-review">
      <VStack className="gap-0.5" testID="reply-draft-your-guidance">
        <Text size="xs" className="font-medium uppercase text-typography-700">
          {REPLY_DRAFT_COPY.yourGuidance}
        </Text>
        <Text size="md" className="font-semibold text-typography-900">
          {callLabel(draft)}
        </Text>
      </VStack>

      <ReplyDraftTourZone segment="review" zone={REPLY_DRAFT_TOUR_ZONES.review.points}>
        <VStack className="gap-1" testID="reply-draft-points">
          <Heading ref={headingRef} size="h2-md" className="font-semibold text-typography-900">
            {REPLY_DRAFT_COPY.pointsHeading(draftedInReplyCount, draftedCount)}
          </Heading>

          <Text size="sm" className="text-typography-700">
            {REPLY_DRAFT_COPY.pointsIntro}
          </Text>
        </VStack>
      </ReplyDraftTourZone>

      <VStack className="gap-1" testID="reply-draft-in-reply">
        <SectionHeading>{REPLY_DRAFT_COPY.inReplyHeading}</SectionHeading>

        {inReply.length > 0 ? (
          renderRows(inReply)
        ) : (
          <Text className="px-2 py-3 text-typography-700">{REPLY_DRAFT_COPY.nothingInReply}</Text>
        )}
      </VStack>

      {notInReply.length > 0 ? (
        <VStack className="gap-1" testID="reply-draft-not-in-reply">
          <SectionHeading>{REPLY_DRAFT_COPY.notInReplyHeading}</SectionHeading>

          {renderRows(notInReply)}
        </VStack>
      ) : null}

      <ReplyDraftTourZone segment="review" zone={REPLY_DRAFT_TOUR_ZONES.review.note}>
        <TextEntry
          label={REPLY_DRAFT_COPY.noteLabel}
          placeholder={REPLY_DRAFT_COPY.notePlaceholder}
          submitLabel={REPLY_DRAFT_COPY.noteSubmit}
          busyLabel={REPLY_DRAFT_COPY.addingNote}
          isBusy={isAddingNote}
          testID="reply-draft-note"
          value={noteText}
          onChangeText={onNoteTextChange}
          onSubmit={onAddNote}
        />
      </ReplyDraftTourZone>

      {isWeb ? (
        <Text className="sr-only" accessibilityLiveRegion="polite" testID="reply-draft-announcer">
          {announcement}
        </Text>
      ) : null}
    </VStack>
  );
}
